// Общая форма «Отправить на доработку» (fd-onboard-18/19). Используется и в
// карточке заявки (доработка всей заявки), и в сводке верификации продавца
// (посекционная ре-модерация с привязкой к docType). Обязательный текст
// замечания, необязательный срок и необязательный выбор раздела; при заданном
// `docType` селект раздела скрыт — форма закреплена за конкретной секцией.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApplicationRemark, SellerDocumentType } from "@/api";
import { useReworkApplication } from "@/api/hooks/use-applications";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Select } from "@/components/ui/select";

/** Разделы «личного дела», к которым можно привязать замечание. */
export const REWORK_DOC_TYPES: SellerDocumentType[] = ["passport_ip", "tax_system", "authority_ul", "other"];
export function ReworkDialog({
  open,
  applicationId,
  shopName,
  docType,
  allowedDocTypes,
  onClose,
  onDone
}: {
  open: boolean;
  applicationId: string;
  shopName?: string;
  /** Когда задан — форма привязана к секции, селект раздела скрыт. */
  docType?: SellerDocumentType;
  /**
   * Разделы, которые у продавца есть (чек-лист верификации). У ИП и
   * самозанятого нет «Полномочий руководителя ЮЛ» — предлагать его нельзя.
   */
  allowedDocTypes?: SellerDocumentType[];
  onClose: () => void;
  onDone?: (remark: ApplicationRemark) => void;
}) {
  const {
    t
  } = useTranslation();
  const rework = useReworkApplication();
  const [message, setMessage] = useState("");
  const [deadline, setDeadline] = useState("");
  const [section, setSection] = useState<SellerDocumentType | "">("");
  const [error, setError] = useState<string | null>(null);
  const reset = () => {
    setMessage("");
    setDeadline("");
    setSection("");
    setError(null);
  };
  const close = () => {
    if (rework.isPending) return;
    reset();
    onClose();
  };
  const submit = async () => {
    if (!message.trim()) {
      setError(t("applications.rework.messageRequired"));
      return;
    }
    setError(null);
    try {
      const chosen = docType ?? (section || undefined);
      const result = await rework.mutateAsync({
        id: applicationId,
        input: {
          message: message.trim(),
          deadline: deadline || undefined,
          docType: chosen
        }
      });
      onDone?.(result.remark);
      reset();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  const title = docType ? t("applications.rework.sectionTitle", {
    section: t(`sellers.personalFile.docType.${docType}`)
  }) : shopName ? `${t("applications.rework.title")} — ${shopName}` : t("applications.rework.title");
  return <ConfirmDialog open={open} busy={rework.isPending} title={title} description={t("applications.rework.body")} confirmLabel={t("applications.rework.submit")} onConfirm={submit} onClose={close}>
			<div className="flex flex-col gap-3">
				<textarea value={message} onChange={e_0 => setMessage(e_0.target.value)} placeholder={t("applications.rework.messagePlaceholder")} className="min-h-24 w-full rounded-md border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
				<label className="flex flex-col gap-1 text-sm">
					<span className="text-muted-foreground">
						{t("applications.rework.deadline")}
					</span>
					<input type="date" min={new Date().toISOString().slice(0, 10)} value={deadline} onChange={e_1 => setDeadline(e_1.target.value)} className="h-9 rounded-md border bg-background px-2 text-sm" />
				</label>
				{!docType && <div className="flex flex-col gap-1 text-sm">
						<span className="text-muted-foreground">
							{t("applications.rework.section")}
						</span>
						<Select aria-label={t("applications.rework.section")} value={section} onChange={e_2 => setSection(e_2.target.value as SellerDocumentType | "")}>
							<option value="">{t("applications.rework.wholeApplication")}</option>
							{REWORK_DOC_TYPES.filter(dt => dt === "other" || !allowedDocTypes || allowedDocTypes.includes(dt)).map(dt_0 => <option key={dt_0} value={dt_0}>
									{t(`sellers.personalFile.docType.${dt_0}`)}
								</option>)}
						</Select>
					</div>}
				{error && <span className="text-sm text-destructive">{error}</span>}
			</div>
		</ConfirmDialog>;
}